import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useHistory } from 'react-router-dom'

import getProductClassDesignSummary, {
  ProductClassDesignSummaryResponse,
} from 'core/application/adminDashboard/benefitDetails/design/productClass/getProductClassDesignSummary'
import { BenefitProductClassResponse } from 'core/application/adminDashboard/benefitDetails/getBenefitProductClasses'
import getUserBenefitProductClass from 'core/application/adminDashboard/benefitDetails/getUserBenefitProductClass'
import getEligibilityGuide from 'core/application/expenses/getEligibilityGuide'
import getUserAvailableExpenseCategories from 'core/application/expenses/getUserAvailableExpenseCategories'
import { Expense, ExpenseTypeFilter } from 'core/application/expenses/getUserAvailableExpenseTypes'

import useCommandRequest from 'common/hooks/useCommandRequest'
import useQueryRequest from 'common/hooks/useQueryRequest'

import { Alert, Col, Row, Skeleton } from 'common/components'
import { ROUTE_PATHS } from 'common/routePaths'

import Navigation from '../../navigation/Navigation'
import { REQUEST_FOR_PREMIUM_EXPENSE_REIMBURSE_PATH } from '../savePremiumExpense'
import CategoryFilter from './CategoryFilter'
import EligibilityGuide from './EligibilityGuide'
import ExpensePremiumIneligibleModal from './ExpensePremiumIneligibleModal'
import ExpenseQuestionaryModal from './ExpenseQuestionaryModal'
import ExpenseSearch from './ExpenseSearch'
import ExpenseTypeList from './ExpenseTypeList'

const ChooseExpenseType = () => {
  const { t: translate } = useTranslation('ee-dashboard')
  const history = useHistory()
  const { planPeriodId } = useParams<any>()
  if (planPeriodId == null) {
    history.push(ROUTE_PATHS.ELIGIBLE_EMPLOYEE_DASHBOARD)
  }

  const [sendGetProductClassDesignSummary, areProductClassesSummaryLoading] = useCommandRequest()
  const [productClassDesignSummary, setProductClassDesignSummary] = useState<ProductClassDesignSummaryResponse | null>(
    null,
  )
  const [sendGetProductClass, areProductClassesLoading] = useCommandRequest()
  const [productClass, setProductClass] = useState<BenefitProductClassResponse | null>(null)
  const [expenseCategories] = useQueryRequest(null, () => getUserAvailableExpenseCategories(planPeriodId))
  const [eligibilityGuide, eligibilityGuideLoading] = useQueryRequest(null, () => getEligibilityGuide())

  const [expensesFilter, setExpensesFilter] = useState<ExpenseTypeFilter>({
    pageNumber: 1,
    pageSize: 10,
    orderColumn: 'name',
    orderDescending: false,
    searchValue: null,
    planPeriodId: planPeriodId,
  })

  const [selectedCategory, setSelectedCategory] = useState<string>(null)
  const [removeSelectedCategory, setRemoveSelectedCategory] = useState<boolean>(false)
  const [showQuestionaryModal, setShowQuestionaryModal] = useState<boolean>(false)
  const [showExpensePremiumIneligibleModal, setShowExpensePremiumIneligibleModal] = useState<boolean>(false)
  const [selectedRowRecord, setSelectedRowRecord] = useState<Expense | null>(null)
  const [totalItemCount, setTotalItemCount] = useState(0)

  useEffect(() => {
    ;(async () => {
      const result = await sendGetProductClass(() => getUserBenefitProductClass())
      if (result.completed && result.data) {
        setProductClass(result.data as BenefitProductClassResponse)
      }
    })()
  }, [])

  useEffect(() => {
    if (productClass) {
      ;(async () => {
        const result = await sendGetProductClassDesignSummary(() => getProductClassDesignSummary(productClass.id))
        if (result.completed && result.data) {
          setProductClassDesignSummary(result.data as ProductClassDesignSummaryResponse)
        }
      })()
    }
  }, [productClass])

  const onExpenseSearch = (searchValue: string) => {
    setRemoveSelectedCategory((prevCategory) => !prevCategory)

    setExpensesFilter({
      ...expensesFilter,
      categoryId: null,
      searchValue: searchValue.normalize(),
    })
  }

  const onClearExpenseSearch = () => {
    setExpensesFilter({
      ...expensesFilter,
      pageNumber: 1,
      categoryId: selectedCategory,
      searchValue: '',
    })
  }

  const onCategoryFilter = (category: string) => {
    setSelectedCategory(category)
    setExpensesFilter({
      ...expensesFilter,
      pageNumber: 1,
      categoryId: category,
      searchValue: '',
    })
  }

  const onPremiumItemPress = () => {
    setShowQuestionaryModal(!showQuestionaryModal)
  }

  const handlePremiumExpenseModalSubmit = (isValidSelection: boolean) => {
    if (isValidSelection) {
      if (selectedRowRecord) {
        history.push(
          `${REQUEST_FOR_PREMIUM_EXPENSE_REIMBURSE_PATH}?expenseTypeId=${selectedRowRecord.id}&planPeriodId=${planPeriodId}`,
          {
            id: selectedRowRecord.id,
            name: selectedRowRecord.name,
            eligibility: selectedRowRecord.eligibilityType,
            planPeriodId: planPeriodId,
          },
        )
      }
    } else {
      setShowExpensePremiumIneligibleModal(true)
    }
  }

  return (
    <>
      <Navigation title={'Get reimbursed'} redirectPath={ROUTE_PATHS.ELIGIBLE_EMPLOYEE_DASHBOARD}>
        <Skeleton active={true} loading={!expenseCategories}>
          {expenseCategories && (
            <>
              <Alert
                showIcon={false}
                className={'mb-32'}
                type="warning"
                message="Expense Submission Tip:"
                description={
                  <div className="color-gray-8">
                    If you're submitting multiple items from one receipt bought on the same day, you can combine them
                    into one submission under any category. No need to submit each item separately. Separate submissions
                    are only required for items bought on a different date.
                  </div>
                }
              />
              <Row gutter={[16, 16]}>
                <Col xs={24} md={24} lg={16} className="border-color-gray-3 p-0">
                  <p className={'semi-bold text-left pt-16 pl-16'}>
                    Get reimbursed for over {totalItemCount} reimbursable expenses
                  </p>
                  <div className="p-16">
                    <ExpenseSearch
                      onClearExpenseSearch={onClearExpenseSearch}
                      onExpenseSearch={onExpenseSearch}
                      expenseFilter={expensesFilter}
                    />
                  </div>
                  <div className="pl-16 pr-16">
                    <CategoryFilter
                      categories={expenseCategories}
                      onCategoryFilter={onCategoryFilter}
                      removeSelected={!removeSelectedCategory}
                    />
                    <p className={'color-gray-8 p-16'}>Total expenses: {totalItemCount}</p>
                  </div>
                  <div className="mb-8">
                    <ExpenseTypeList
                      expenseTypeFilters={expensesFilter}
                      planPeriodId={planPeriodId}
                      onPremiumItemPress={onPremiumItemPress}
                      onRowSelected={(expense) => setSelectedRowRecord(expense)}
                      onSearchCompleted={(itemCount) => setTotalItemCount(itemCount)}
                    ></ExpenseTypeList>
                  </div>
                </Col>
                <Col xs={24} md={24} lg={8}>
                  <Skeleton loading={eligibilityGuideLoading}>
                    {eligibilityGuide && (
                      <EligibilityGuide
                        title={translate('get-reimburse.expense-type.eligibility-guide-title')}
                        items={eligibilityGuide}
                      />
                    )}
                  </Skeleton>
                </Col>
              </Row>

              <Row gutter={[16, 16]}>
                <Col xs={24} md={24} lg={16}>
                  {selectedRowRecord && showQuestionaryModal && (
                    <ExpenseQuestionaryModal
                      isModalVisible={showQuestionaryModal}
                      setIsModalVisible={setShowQuestionaryModal}
                      record={selectedRowRecord}
                      onFormSubmit={handlePremiumExpenseModalSubmit}
                      loading={areProductClassesSummaryLoading || areProductClassesLoading}
                    />
                  )}
                  <ExpensePremiumIneligibleModal
                    isModalVisible={showExpensePremiumIneligibleModal}
                    setIsModalVisible={setShowExpensePremiumIneligibleModal}
                  />
                </Col>
              </Row>
            </>
          )}
        </Skeleton>
      </Navigation>
    </>
  )
}

export default ChooseExpenseType
